import * as z from 'zod/v4';

import { Operator, OperatorSchema } from './operator';

/**
 * An expression representing a reference to a field in a concrete table or CTE
 */
export type FieldExpression = {
  fieldId: string;
  table?: string;
  alias?: string;
};

/** The valid types for a literal expression value or array */
export type LiteralValue = string | number | boolean | null;

/** An expression representing a literal value */
export type LiteralExpression = {
  literal:
    | LiteralValue
    | Array<number | null>
    | Array<string | null>
    | Array<boolean | null>;
  alias?: string;
};

/** An expression representing an operator function call */
export type OperatorExpression = {
  operator: Operator;
  operands: ReadonlyArray<Expression>;
  alias?: string;
};

/**
 * An expression representing a legacy metric expression, used in custom metrics.
 * TODO(DNO-837): Deprecate and remove this type or move to the UI component.
 */
export type MetricExpression = {
  metricId: string;
  filter?: Expression;
  alias?: string;
  table?: string;
};

/** An expression representing a reference to a field in a semantic schema */
export type SemanticExpression = {
  semanticId: string;
  table: string | null;
  alias?: string;
};

export type Expression =
  | FieldExpression
  | MetricExpression
  | SemanticExpression
  | OperatorExpression
  | LiteralExpression;

const biFieldExpressionSchema = z.object({
  fieldId: z.string(),
  table: z.string().optional(),
  alias: z.string().optional(),
}) satisfies z.ZodType<FieldExpression>;

const biLiteralExpressionSchema = z.object({
  literal: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.string().nullable()),
    z.array(z.number().nullable()),
    z.array(z.boolean().nullable()),
  ]),
  alias: z.string().optional(),
}) satisfies z.ZodType<LiteralExpression>;

export const biSemanticExpressionSchema = z.object({
  semanticId: z.string(),
  table: z.string().nullable(),
  alias: z.string().optional(),
}) satisfies z.ZodType<SemanticExpression>;

// Metric expressions must explicitly refer to a table to disambiguate
// between different metrics with the same name
export const biSemanticMetricExpressionSchema =
  biSemanticExpressionSchema.extend({ table: z.string() });

// Note that because of the recursion here, this is not fully guaranteed to throw a type error
// if it diverges from the Expression union defined in pure types above - in particular, new
// members in the type or might not throw an error if omitted here, and vice versa
export const biExpressionSchema: z.ZodType<Expression> = z.union([
  biFieldExpressionSchema,
  biLiteralExpressionSchema,
  biSemanticExpressionSchema,
  // MetricExpression - inlined for recursion
  z.object({
    metricId: z.string(),
    filter: z.lazy(() => biExpressionSchema).optional(),
    alias: z.string().optional(),
  }),
  // OperatorExpression - inlined for recursion
  // We need to keep this in sync with OperatorExpressionSchema in workspaces/shared-universal/csrd/dataRequirements.ts.
  z.object({
    operator: OperatorSchema,
    operands: z.array(z.lazy(() => biExpressionSchema)).readonly(),
    alias: z.string().optional(),
  }),
]);

export type AliasedExpression = Expression & { alias: string };
